import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import LiveClass from './LiveClass';
// import RecordedVideo from './RecordedVideo';
import Assignments from './Assignments';
import Assessments from './Assessments';
// import Cummunity from './Cummunity';
import { getTraineeClassroomsDashboardData } from '../../../services/userService';
import { decodeData } from '../../../utils/encodeAndEncode';

function UserClassroom() {
  const { encodedClassroomID } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const classroom_id = decodeData(encodedClassroomID);
  const urlActiveTab = searchParams.get('active-tab');
  const normalizedUrlTab = urlActiveTab === 'assessments' ? 'assessment' : urlActiveTab;
  const allowedTabs = ['assignments', 'assessment', 'live'];
  const initialTab = normalizedUrlTab && allowedTabs.includes(normalizedUrlTab) ? normalizedUrlTab : 'live';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [classroomData, setClassroomData] = useState(null);
  const [classroomName, setClassroomName] = useState(null);
  const [tabVisibility, setTabVisibility] = useState([]);
  const [loading, setLoading] = useState(true);

  // Check if all tabs are disabled
  const areAllTabsDisabled = tabVisibility.length > 0 &&
    tabVisibility.every(tab => tab.is_visible === "false");

  useEffect(() => {
    const fetchClassroomData = async () => {
      try {
        const response = await getTraineeClassroomsDashboardData({ class_id: classroom_id });
        // console.log("Classroom Data ------------", response);

        // Handle different response structures
        let classroomData = null;
        if (response.data.status === 200 && response.data.data) {
          classroomData = response.data.data;
        } else if (response.data && !response.data.status) {
          // Direct data response
          classroomData = response.data;
        }

        if (classroomData) {
          // console.log('Group data from API:', classroomData.group);
          setClassroomData(classroomData);
          setClassroomName(classroomData.cls_name);

          // Set tab visibility
          if (classroomData.is_visibility) {
            setTabVisibility(classroomData.is_visibility);

            // Only set default tab if no URL parameter is present
            if (!urlActiveTab) {
              // Always default to live sessions if visible
              const liveTab = classroomData.is_visibility.find(
                tab => tab.tab_name === 'liveclass' && tab.is_visible === "true"
              );
              if (liveTab) {
                setActiveTab('live');
              } else {
                // Fallback to first visible tab if live is not visible
                const visibleTabNames = ['assignment', 'assessment'];
                const firstVisibleTab = classroomData.is_visibility.find(
                  tab => tab.is_visible === "true" && visibleTabNames.includes(tab.tab_name)
                );
                if (firstVisibleTab) {
                  const tabMapping = {
                    'assignment': 'assignments',
                    'assessment': 'assessment'
                  };
                  setActiveTab(tabMapping[firstVisibleTab.tab_name] || 'live');
                }
              }
            }
          }

          // Set group data
          if (classroomData.group) {
            // setGroupData({ // This line was removed as per the new_code, as groupData state was removed
            //   group_id: classroomData.group.group_id,
            //   group_name: classroomData.group.group_name
            // });
          }
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching classroom data:', error);
        setLoading(false);
      }
    };

    fetchClassroomData();
  }, [encodedClassroomID, urlActiveTab]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  // Helper function to check if a tab is visible
  const isTabVisible = (tabName) => {
    // If tabVisibility is empty (not loaded yet), show all tabs by default
    if (tabVisibility.length === 0) {
      return true;
    }

    const tab = tabVisibility.find(t => t.tab_name === tabName);
    return tab ? tab.is_visible === "true" : false;
  };

  if (loading) {
    return (
      <div className="min-h-[200px] flex flex-col items-center justify-center p-8">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-[#7083FF]" role="status" aria-label="Loading" />
        <p className="mt-3 text-sm text-gray-500">Loading classroom...</p>
      </div>
    );
  }

  return (
    <div className="max-w-screen-2xl mx-auto px-3 sm:px-4 py-6">
      {/* Back Button */}
      <button
        type="button"
        onClick={() => navigate('/user/AllClassroom')}
        className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-[#7083FF] transition-colors py-2 px-2 rounded-lg hover:bg-white mb-4"
      >
        <Icon icon="mdi:arrow-left" width="20" height="20" />
        Back to Live Sessions
      </button>

      <div className="rounded-2xl border border-gray-100 bg-white shadow-sm overflow-hidden">
        <div className="border-b border-gray-100 bg-gray-50/50 px-4 sm:px-6 py-4">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3">
            <h1 className="text-xl font-semibold text-gray-900 flex-1 min-w-0 truncate">{classroomData?.cls_name || 'Classroom'}</h1>

            {!areAllTabsDisabled && (
              <div className="flex flex-wrap gap-2 lg:justify-end">
                {isTabVisible('liveclass') && (
                  <button
                    type="button"
                    className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                      activeTab === 'live'
                        ? 'bg-[#7083FF] text-white shadow-md shadow-[#7083FF]/25'
                        : 'bg-white text-gray-600 hover:bg-white hover:text-[#7083FF] border border-gray-200 hover:border-[#7083FF]/40 shadow-sm'
                    }`}
                    onClick={() => handleTabChange('live')}
                  >
                    <Icon icon="mdi:video" width="22" height="22" />
                    Live Sessions
                  </button>
                )}
                {isTabVisible('assignment') && (
                  <button
                    type="button"
                    className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                      activeTab === 'assignments'
                        ? 'bg-[#7083FF] text-white shadow-md shadow-[#7083FF]/25'
                        : 'bg-white text-gray-600 hover:bg-white hover:text-[#7083FF] border border-gray-200 hover:border-[#7083FF]/40 shadow-sm'
                    }`}
                    onClick={() => handleTabChange('assignments')}
                  >
                    <Icon icon="mdi:file-document" width="22" height="22" />
                    Assignments
                  </button>
                )}
                {isTabVisible('assessment') && (
                  <button
                    type="button"
                    className={`inline-flex items-center gap-2.5 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
                      activeTab === 'assessment'
                        ? 'bg-[#7083FF] text-white shadow-md shadow-[#7083FF]/25'
                        : 'bg-white text-gray-600 hover:bg-white hover:text-[#7083FF] border border-gray-200 hover:border-[#7083FF]/40 shadow-sm'
                    }`}
                    onClick={() => handleTabChange('assessment')}
                  >
                    <Icon icon="mdi:clipboard-text" width="22" height="22" />
                    Assessment
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {areAllTabsDisabled ? (
          <div className="p-8 sm:p-10 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 text-gray-400 mb-4">
              <Icon icon="fluent:presence-blocked-24-regular" width="32" height="32" />
            </div>
            <h2 className="text-lg font-semibold text-gray-700 mb-2">No active tabs</h2>
            <p className="text-gray-500 text-sm max-w-sm mx-auto">
              Classroom features are currently disabled. Check back later or contact your instructor.
            </p>
          </div>
        ) : (
          <>
            <div className="p-4 sm:p-6 bg-white min-h-[320px]">
              {activeTab === 'live' && (
                <div className="live-class">
                  <LiveClass classroom_id={classroom_id} classroomName={classroomName} />
                </div>
              )}
              {activeTab === 'assignments' && (
                <div className="assignments">
                  <Assignments classroom_id={classroom_id} classroomName={classroomName} />
                </div>
              )}
              {activeTab === 'assessment' && (
                <div className="assessment">
                  <Assessments classroom_id={classroom_id} classroomName={classroomName} />
                </div>
              )}
              {/* {activeTab === 'recorded' && <RecordedVideo classroom_id={classroom_id} />} */}
              {/* {activeTab === 'community' && <Cummunity ... />} */}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default UserClassroom;
